import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Globe, Bell, Palette, Save, Check, ArrowLeft } from 'lucide-react';

function PreferenceCenter({ user, onBack }) {
  const [preferences, setPreferences] = useState({
    language: 'en',
    theme: 'light',
    notifications_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const loadPreferences = useCallback(async () => {
    try {
      const response = await axios.get('/api/user/preferences');
      setPreferences({
        language: response.data.language || 'en',
        theme: response.data.theme || 'light',
        notifications_enabled: response.data.notifications_enabled !== false
      });
    } catch (err) {
      console.error('Failed to load preferences:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.put('/api/user/preferences', preferences);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save preferences:', err);
      alert('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const languages = [
    { code: 'en', label: 'English', flag: '🇺🇸' },
    { code: 'pt', label: 'Português', flag: '🇧🇷' },
    { code: 'es', label: 'Español', flag: '🇪🇸' }
  ];

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        color: '#64748b'
      }}>
        Loading preferences...
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '32px',
      maxWidth: '800px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: '#64748b',
            cursor: 'pointer',
            padding: '8px 0',
            fontSize: '0.9rem',
            marginBottom: '16px'
          }}
        >
          <ArrowLeft size={18} />
          Back to Dashboard
        </button>
        <h1 style={{ 
          margin: 0, 
          fontSize: '1.75rem', 
          fontWeight: '700', 
          color: '#1a1a2e',
          marginBottom: '8px'
        }}>
          Preference Center
        </h1>
        <p style={{ margin: 0, color: '#64748b', fontSize: '0.95rem' }}>
          Customize your experience and settings
        </p>
      </div>

      {/* Language Section */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #e8ecf1',
        padding: '24px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Globe size={20} color="white" />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1a1a2e' }}>
              Language
            </h2>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
              Choose your preferred language for AI agent responses
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setPreferences({ ...preferences, language: lang.code })}
              style={{
                padding: '16px',
                borderRadius: '12px',
                border: preferences.language === lang.code 
                  ? '2px solid #667eea' 
                  : '2px solid #e2e8f0',
                background: preferences.language === lang.code 
                  ? 'linear-gradient(135deg, #667eea10 0%, #764ba210 100%)' 
                  : 'white',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
            >
              <span style={{ fontSize: '2rem' }}>{lang.flag}</span>
              <span style={{ 
                fontWeight: preferences.language === lang.code ? '600' : '500',
                color: preferences.language === lang.code ? '#667eea' : '#334155',
                fontSize: '0.95rem'
              }}>
                {lang.label}
              </span>
              {preferences.language === lang.code && (
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#667eea',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Check size={12} color="white" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Notifications Section */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #e8ecf1',
        padding: '24px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Bell size={20} color="white" />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1a1a2e' }}>
              Notifications
            </h2>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
              Manage how you receive updates and alerts
            </p>
          </div>
        </div>

        <label style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px',
          background: '#f8fafc',
          borderRadius: '10px',
          cursor: 'pointer'
        }}>
          <div>
            <span style={{ fontWeight: '500', color: '#334155' }}>Enable notifications</span>
            <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
              Receive alerts for task updates and deadlines
            </p>
          </div>
          <div 
            onClick={() => setPreferences({ ...preferences, notifications_enabled: !preferences.notifications_enabled })}
            style={{
              width: '48px',
              height: '26px',
              borderRadius: '13px',
              background: preferences.notifications_enabled ? '#667eea' : '#cbd5e1',
              padding: '2px',
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
          >
            <div style={{
              width: '22px',
              height: '22px',
              borderRadius: '50%',
              background: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transform: preferences.notifications_enabled ? 'translateX(22px)' : 'translateX(0)',
              transition: 'transform 0.2s'
            }} />
          </div>
        </label>
      </div>

      {/* Theme Section (Future) */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #e8ecf1',
        padding: '24px',
        marginBottom: '32px',
        opacity: 0.6
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Palette size={20} color="white" />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#1a1a2e' }}>
              Theme
            </h2>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>
              Customize the appearance of the application
            </p>
          </div>
          <span style={{
            padding: '4px 10px',
            background: '#f1f5f9',
            borderRadius: '6px',
            fontSize: '0.75rem',
            fontWeight: '600',
            color: '#64748b'
          }}>
            Coming Soon
          </span>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: saved 
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
            transition: 'all 0.2s'
          }}
        >
          {saved ? (
            <>
              <Check size={18} />
              Saved!
            </>
          ) : (
            <>
              <Save size={18} />
              {saving ? 'Saving...' : 'Save Preferences'}
            </>
          )}
        </button>
      </div>

      </div>
  );
}

export default PreferenceCenter;
